import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, FamilyIcon, Avatar, SearchInput, Icon, useEscape, useToast } from '../../components/ui';
import { typeOfExamType } from '../../config/assessmentFamilies';
import { campaignAPI, examSetAPI, batchAPI } from '../../services/api';
import { getPublicApiOrigin } from '../../config/env';
import { lowerEmail } from './derive';
import { sendBulkInvite } from './actions';
import NewCandidatesPanel, { emptyRow, rowState } from './NewCandidatesPanel';

async function fetchSynced(campaignId) {
  const token = localStorage.getItem('token');
  const res = await fetch(`${getPublicApiOrigin()}/api/integration/campaigns/${campaignId}/synced-candidates`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  return res.ok ? res.json() : { candidates: [] };
}

/**
 * Prototype batchDialog() shell with T0 item K inside: tabs "New candidate" (default) /
 * "From ReachOut" / "On this position", "Also in: X" warnings, and "Send invites now"
 * (on by default) which calls assign, then the existing bulk invite.
 * `batchSection` (T10, feature flag) renders the new/existing batch controls and
 * `onBeforeAssign` lets it create the batch first.
 */
export default function AddCandidatesDialog({
  campaign, examSet, data, onClose, onDone, batchSection = null, submitLabel, onBeforeAssign, blocked = '', allowEmpty = false,
}) {
  const toast = useToast();
  const boxRef = useRef(null);
  useEscape(onClose, { ref: boxRef });
  const [tab, setTab] = useState('new');
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState(() => new Set()); // person keys
  const [pickedSynced, setPickedSynced] = useState(() => new Set());
  const [synced, setSynced] = useState(null); // ReachOut candidates not on the position yet
  // Every ReachOut candidate email of this position, imported or not — tells where a
  // position candidate came from (there is no source column on candidate rows).
  const [reachoutEmails, setReachoutEmails] = useState(() => new Set());
  const [sendNow, setSendNow] = useState(true);
  // Invites email real people: when they are about to go out, ask first.
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [newMode, setNewMode] = useState('manual');
  const [newRows, setNewRows] = useState(() => [emptyRow()]);
  const [newFiles, setNewFiles] = useState([]);
  const t = typeOfExamType(examSet.exam_type);

  useEffect(() => { boxRef.current?.focus(); }, []);

  useEffect(() => {
    let cancelled = false;
    fetchSynced(campaign.id)
      .then((d) => {
        if (cancelled) return;
        const native = new Set(data.candidates.filter((c) => !String(c.id).startsWith('synced-')).map((c) => lowerEmail(c.email)));
        const all = (d.candidates || []).filter((sc) => sc.email);
        setReachoutEmails(new Set(all.map((sc) => lowerEmail(sc.email))));
        setSynced(all.filter((sc) => !native.has(lowerEmail(sc.email))));
      })
      .catch(() => { if (!cancelled) setSynced([]); });
    return () => { cancelled = true; };
  }, [campaign.id, data.candidates]);

  const examSetName = useMemo(() => Object.fromEntries(data.examSets.map((es) => [es.id, es.name])), [data.examSets]);

  /** People who already have a native candidate row on this position. */
  const onPosition = useMemo(() => data.people
    .map((p) => {
      const native = p.rows.filter((r) => !String(r.id).startsWith('synced-'));
      if (!native.length) return null;
      const hereRow = native.find((r) => r.exam_set_id === examSet.id);
      const here = Boolean(hereRow);
      const hereBatch = hereRow?.batch_id ? (data.batchesById || {})[hereRow.batch_id]?.name : null;
      const elsewhere = [...new Set(native.filter((r) => r.exam_set_id && r.exam_set_id !== examSet.id).map((r) => examSetName[r.exam_set_id] || 'another test'))];
      // An unassigned row is moved; otherwise the backend clones a new row for this test.
      const row = native.find((r) => !r.exam_set_id) || native[0];
      return { p, here, hereBatch, elsewhere, row };
    })
    .filter(Boolean), [data.people, data.batchesById, examSet.id, examSetName]);

  const q = search.trim().toLowerCase();
  const match = (name, email) => !q || `${name || ''} ${email || ''}`.toLowerCase().includes(q);
  const toggle = (setter) => (key) => setter((prev) => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const readyRows = newRows.filter((r) => rowState(r) === 'ok');
  const badRows = newRows.filter((r) => !['ok', 'empty'].includes(rowState(r)));
  const newCount = readyRows.length + newFiles.length;
  const total = picked.size + pickedSynced.size + newCount;

  const submit = async (send = sendNow) => {
    setConfirming(false);
    if ((!total && !allowEmpty) || blocked) return;
    if (badRows.length) {
      setTab('new');
      setNewMode('manual');
      setError('Fix the highlighted rows, or clear them, before adding.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const ctx = onBeforeAssign ? await onBeforeAssign() : null;
      const rowIds = onPosition.filter((x) => picked.has(x.p.key)).map((x) => x.row.id);
      const emails = new Set([
        ...onPosition.filter((x) => picked.has(x.p.key)).map((x) => x.p.key),
        ...(synced || []).filter((sc) => pickedSynced.has(sc.id)).map((sc) => lowerEmail(sc.email)),
      ]);
      const batchId = ctx?.batchId || null;
      const calls = [];
      if (rowIds.length) {
        calls.push(batchId
          ? batchAPI.assignCandidates(campaign.id, examSet.id, rowIds, batchId)
          : examSetAPI.assignCandidates(campaign.id, examSet.id, rowIds));
      }
      if (pickedSynced.size) {
        calls.push(batchId
          ? batchAPI.importSyncedCandidates(campaign.id, examSet.id, [...pickedSynced], batchId)
          : examSetAPI.importSyncedCandidates(campaign.id, examSet.id, [...pickedSynced]));
      }
      await Promise.all(calls);

      // New people typed in or uploaded here: created on this position and assigned to this interview.
      const createdIds = [];
      const failed = [];
      for (const r of readyRows) {
        const fd = new FormData();
        fd.append('name', r.name.trim());
        fd.append('email', r.email.trim());
        if (r.phone.trim()) fd.append('phone', r.phone.trim());
        if (r.file) fd.append('resume', r.file);
        fd.append('exam_set_id', examSet.id);
        try {
          const res = await campaignAPI.createCandidateWithResume(campaign.id, fd);
          if (res.data?.id) createdIds.push(res.data.id);
          emails.add(lowerEmail(r.email));
        } catch (err) {
          failed.push(`${r.email.trim()}: ${err.response?.data?.detail || err.message}`);
        }
      }
      if (newFiles.length) {
        try {
          const res = await campaignAPI.uploadResumesForCandidates(campaign.id, newFiles, [], [], examSet.id);
          (res.data?.candidates || []).forEach((c) => { createdIds.push(c.id); if (c.email) emails.add(lowerEmail(c.email)); });
          (res.data?.failed || []).forEach((f) => failed.push(`${f.filename}: ${f.error}`));
        } catch (err) {
          failed.push(`Résumé upload: ${err.response?.data?.detail || err.message}`);
        }
      }
      if (batchId && createdIds.length) {
        await batchAPI.assignCandidates(campaign.id, examSet.id, createdIds, batchId);
      }
      const added = picked.size + pickedSynced.size + createdIds.length;

      let sent = 0;
      if (send && added) {
        const fresh = await campaignAPI.listCandidates(campaign.id);
        const rows = Array.isArray(fresh.data) ? fresh.data : (fresh.data?.data || []);
        const ids = rows
          .filter((r) => r.exam_set_id === examSet.id && String(r.invite_status || 'pending').toLowerCase() === 'pending' && emails.has(lowerEmail(r.email)))
          .map((r) => r.id);
        if (ids.length) {
          const res = await sendBulkInvite(ids);
          sent = res.sent_count ?? ids.length;
        }
      }
      await onDone?.(ctx);
      if (!total && ctx?.created) toast(`Batch created for ${examSet.name}`);
      else if (added) toast(send ? `${added} added · ${sent} invite${sent === 1 ? '' : 's'} sent` : `${added} candidate${added === 1 ? '' : 's'} added to ${examSet.name}`);
      if (failed.length) {
        // Keep the dialog open so the person can see what didn't go in and retry just those.
        setPicked(new Set());
        setPickedSynced(new Set());
        setNewRows([emptyRow()]);
        setNewFiles([]);
        setTab('new');
        setError(`${failed.length} could not be added:\n${failed.join('\n')}`);
        return;
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to assign candidates.');
    } finally {
      setBusy(false);
    }
  };

  const positionList = onPosition.filter((x) => match(x.p.name, x.p.email));
  const fromReachout = (x) => reachoutEmails.has(lowerEmail(x.p.email));
  // ReachOut tab: not-yet-imported candidates plus ReachOut candidates already on the
  // position (picked through the same selection as "On this position", never twice).
  const reachoutOnPosition = positionList.filter(fromReachout);
  const reachoutCount = synced === null ? null : synced.length + onPosition.filter(fromReachout).length;
  const syncedList = (synced || []).filter((sc) => match(sc.name, sc.email));

  /** One position candidate. showSource: "ReachOut" / "Added here" label (On this position tab);
   *  the ReachOut tab shows "On position" instead, since everything there is from ReachOut. */
  const positionRow = ({ p, here, hereBatch, elsewhere, ...rest }, showSource) => (
    <label key={p.key} className={`wz-pick${here ? ' disabled' : ''}`}>
      <input type="checkbox" disabled={here} checked={here || picked.has(p.key)} onChange={() => toggle(setPicked)(p.key)} />
      <Avatar name={p.name} size={30} />
      <span style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, gap: 1 }}>
        <span style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {p.name}
          {showSource
            ? <span className={`wz-tag ${fromReachout({ p, ...rest }) ? 'ai' : 'muted'}`}>{fromReachout({ p, ...rest }) ? 'ReachOut' : 'Added here'}</span>
            : <span className="wz-tag muted">On position</span>}
        </span>
        <span className="wz-sub wz-ellipsis" style={{ fontSize: 12 }}>{p.email}</span>
        {!here && elsewhere.length > 0 && (
          <span
            className="wz-tag warn"
            style={{ alignSelf: 'flex-start', marginTop: 4, whiteSpace: 'normal' }}
            title="Adding them here creates a separate entry for this interview with its own invite link — their other assignment is unaffected."
          >
            Also in: {elsewhere.join(', ')}
          </span>
        )}
      </span>
      {here && <span className="wz-tag ok">{hereBatch ? `In ${hereBatch}` : 'In this interview'}</span>}
    </label>
  );

  return (
    <>
      <div className="wz-scrim" style={{ zIndex: 1048 }} onClick={onClose} />
      <div className="wz-dialog" style={{ zIndex: 1049 }} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div ref={boxRef} tabIndex={-1} className="wz-dialog-box" role="dialog" aria-modal="true" aria-label={`Add candidates to ${examSet.name}`} style={{ width: 'min(560px, 100%)' }}>
          <div className="wz-dialog-head">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <h2 style={{ fontSize: 18 }}>Add candidates</h2>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }} className="wz-sub"><FamilyIcon fam={t.fam} size="s" />{examSet.name}</span>
            </div>
            <Button iconOnly icon="x" aria-label="Close" onClick={onClose} />
          </div>
          <div style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 16, overflow: 'auto' }}>
            {batchSection}
            {error && <div className="wz-error" role="alert" style={{ whiteSpace: 'pre-line' }}>{error}</div>}
            {blocked && <div className="wz-error" role="alert">{blocked}</div>}
            <div className="wz-subtabs" role="tablist" aria-label="Where candidates come from">
              {[['new', 'New candidate', newCount || null], ['reachout', 'From ReachOut', reachoutCount], ['position', 'On this position', onPosition.length]].map(([k, l, n]) => (
                <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>
                  {l}{n != null && <span className="wz-count">{n}</span>}
                </button>
              ))}
            </div>
            {tab !== 'new' && (
              <SearchInput label="Search candidates" placeholder="Search name or email" value={search} onChange={setSearch} width="100%" />
            )}
            {tab === 'position' && (
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                {positionList.length ? positionList.map((x) => positionRow(x, true)) : <p className="wz-sub" style={{ padding: '16px 0' }}>{onPosition.length ? 'No candidates match.' : 'No candidates on this position yet.'}</p>}
              </div>
            )}
            {tab === 'reachout' && (
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                {reachoutOnPosition.map((x) => positionRow(x, false))}
                {synced === null ? <p className="wz-sub">Loading…</p> : (syncedList.length || reachoutOnPosition.length) ? syncedList.map((sc) => (
                  <label key={sc.id} className="wz-pick">
                    <input type="checkbox" checked={pickedSynced.has(sc.id)} onChange={() => toggle(setPickedSynced)(sc.id)} />
                    <Avatar name={sc.name || sc.email} size={30} />
                    <span style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                      <span style={{ fontWeight: 500 }}>{sc.name || sc.email}</span>
                      <span className="wz-sub wz-ellipsis" style={{ fontSize: 12 }}>{sc.email}</span>
                    </span>
                    <span className={`wz-tag ${sc.is_shortlisted ? 'ok' : 'muted'}`}>{sc.is_shortlisted ? 'Shortlisted' : 'Not shortlisted'}</span>
                  </label>
                )) : <p className="wz-sub" style={{ padding: '16px 0' }}>{reachoutCount ? 'No candidates match.' : 'No ReachOut candidates for this position.'}</p>}
              </div>
            )}
            {tab === 'new' && (
              <NewCandidatesPanel
                mode={newMode}
                setMode={setNewMode}
                rows={newRows}
                setRows={setNewRows}
                files={newFiles}
                setFiles={setNewFiles}
                examSetName={examSet.name}
                disabled={busy}
              />
            )}
          </div>
          {confirming ? (
            <div className="wz-dialog-foot wz-confirm" role="alertdialog" aria-label="Send invites now?">
              <span style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span className="wz-confirm-ic" aria-hidden="true"><Icon name="mail" /></span>
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <b>Send {total} invite{total === 1 ? '' : 's'} now?</b>
                  <span className="wz-sub">
                    {total === 1 ? 'This candidate gets' : `These ${total} candidates get`} an email with their exam link right away{batchSection ? ', valid for this batch’s window' : ''}.
                  </span>
                </span>
              </span>
              <span style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <Button onClick={() => setConfirming(false)}>Back</Button>
                <Button onClick={() => submit(false)}>Add without sending</Button>
                <Button variant="primary" icon="mail" autoFocus onClick={() => submit(true)}>Yes, send {total} invite{total === 1 ? '' : 's'}</Button>
              </span>
            </div>
          ) : (
            <div className="wz-dialog-foot">
              <label className={`wz-check wz-sendnow${sendNow ? ' on' : ''}`}>
                <input type="checkbox" checked={sendNow} onChange={(e) => setSendNow(e.target.checked)} />
                Send invites now
                {sendNow && <span className="wz-sub" style={{ fontSize: 12 }}>· emails go out when you add</span>}
              </label>
              <span style={{ display: 'flex', gap: 8 }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                  variant="primary"
                  disabled={busy || (!total && !allowEmpty) || !!blocked}
                  onClick={() => {
                    if (sendNow && total && !badRows.length) setConfirming(true);
                    else submit();
                  }}
                >
                  {busy ? 'Adding…' : (submitLabel ? submitLabel(sendNow) : `${sendNow ? 'Add and send invites' : 'Add candidates'}${total ? ` (${total})` : ''}`)}
                </Button>
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
